/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Fader/FormFader";
import Grid from "@mui/material/Grid";
import FormSubmit from "../FormSubmit/FormSubmit";
import { useForm } from "../../../../provider/Form/FormProvider";
import ValidatorTextField from "../../../FormFields/ValidatorTextField";
import { requiredValidator } from "../../../../services/validationRules";
import FormLegend from "../FormLegend/FormLegend";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import wizardStyle from "../../wizardStyle";
import { Box } from "@mui/material";
import WaitingForAiResponse from "../WaitingForAiResponse/WaitingForAiResponse";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import { useWaitForAsyncFields } from "../../../../hooks/useWairForAsyncFields";

type ConcernSummaryProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

const REQUIRED_FIELDS = ["finalClientDescriptionAndObjectives"] as const;
type RequiredField = typeof REQUIRED_FIELDS[number];

export default function ConcernSummary({ submitProps, product }: ConcernSummaryProps) {
  const { handleSubmit } = useForm();

  const { isWaiting: isWaitingForAiResponse } = useWaitForAsyncFields<AbstractCase, RequiredField>({
    product,
    requiredFields: REQUIRED_FIELDS,
    timeoutMs: 30_000,
  });

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Zusammenfassung"}
        headline={"Wir fassen Ihr Anliegen abschließend zusammen"}
        text={"Ihr Anliegen wird gerade abschließend zusammengefasst. Dies kann bis zu einer Minute dauern. Bitte laden Sie die Seite nicht neu."}
      />
    );
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline"
            }}>
            <Grid size={12}>
              <FormLegend>
                <strong>Wir fassen Ihr Anliegen abschließend zusammen</strong>
              </FormLegend>
            </Grid>
            <Grid
              size={12}
              sx={[{
                mb: 4
              }, wizardStyle.gridTopSpaced]}>
              <FormLegend>
                Bitte prüfen Sie, ob ihr Anliegen korrekt aufgenommen wurde. Sie können gerne Informationen ergänzen oder anpassen. Im nächsten Schritt haben
                Sie noch die Möglichkeit uns Dokumente zukommen zu lassen.
              </FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="finalClientDescriptionAndObjectives"
                label="Zusammenfassung des Anliegens"
                InputLabelProps={{
                  shrink: true,
                }}
                multiline
                rows={10}
                validators={[requiredValidator("Bitte ergänzen Sie die notwendigen Informationen.")]}
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
